/** English strings for the work queue and outgoing webhooks. The Vietnamese file must have exactly the same keys. */
export const work = {
  crumbs: { compliance: 'Compliance', queue: 'Work queue', settings: 'Integrations' },

  kind: {
    UPSTREAM_REGISTRANT_UNVERIFIED: 'Registrant unverified with the upstream registrar',
    EKYC_MISMATCH: 'eKYC mismatch',
    EKYC_FAILED: 'eKYC failed',
    VERIFICATION_INCONCLUSIVE: 'Verification inconclusive',
    DOSSIER_MANUAL_REVIEW: 'Policy requires manual review',
    SUBJECT_CHANGED_AFTER_DOSSIER: 'Registrant changed after dossier',
    VNNIC_SYNC_MISSING: 'Never synced to VNNIC',
    VNNIC_SYNC_STUCK: 'VNNIC sync stuck',
    VNNIC_SYNC_REJECTED: 'VNNIC rejected / correction',
    VNNIC_SYNC_DRIFT: 'Dossier differs from VNNIC copy',
    DECLARATION_REVIEW: 'Declaration review',
    MANUAL_ACTIVATION: 'Manual activation (.vn)',
    WORKFLOW_FAILED: 'Registration / renewal failed',
    WORKFLOW_MANUAL_REVIEW: 'Workflow waiting for a person',
    REGISTRY_TRANSACTION: 'Registry command unresolved',
    RECONCILIATION_ISSUE: 'Registry drift',
    VNNIC_LIST_MISMATCH: 'VNNIC list mismatch',
    COVERAGE_GAP: 'Coverage gap',
    NOTIFICATION_FAILED: 'E-mail failed',
    WEBHOOK_ENDPOINT_DISABLED: 'Webhook endpoint disabled',
    HOLDER_TRANSFER_REVIEW: 'Holder transfer review',
    SUBJECT_CONVERSION_REVIEW: 'Subject conversion review',
    NAME_CORRECTION_REVIEW: 'Name correction review',
    VNNIC_PANEL_NAME_CHANGE: 'Enter name change in the VNNIC panel',
    VNNIC_PANEL_REGISTRY_LOCK: 'Set registry lock in the VNNIC panel',
    VNNIC_PANEL_REGISTRY_UNLOCK: 'Remove registry lock in the VNNIC panel',
    VNNIC_PANEL_OVERDUE: 'VNNIC panel change overdue',
    REGISTRAR_SIGNATURE_MISMATCH: 'Registrar-signed file does not match its dossier',
    REGISTRAR_SIGNATURE_PRECHECK: 'Dossier check failed before the registrar signs',
    POST_OPERATION_ATTENTION: 'Post-operation step refused / failed',
    ADDON_ATTENTION: 'An add-on asks staff to look',
    EINVOICE_RUN_FAILED: 'E-invoice not issued by the 17:00 run',
    PROTECTED_CHANGE_PENDING: 'Protected domain: change awaiting confirmation',
    PROTECTED_DOMAIN_EXPIRY: 'Protected domain: expiry stage',
    PROTECTED_TRANSFER_OUT: 'Protected domain: transfer-out request',
  },
  severity: { CRITICAL: 'Critical', HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' },
  status: { OPEN: 'Open', RESOLVED: 'Resolved', AUTO_RESOLVED: 'Closed by the system', CLOSED: 'Closed' },
  resolution: {
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CORRECTION_REQUESTED: 'Correction requested',
    DONE: 'Done',
    AUTO_RESOLVED: 'Problem went away',
  },
  field: { MATCH: 'Match', MISMATCH: 'Mismatch', UNREADABLE: 'Unreadable', NOT_DECLARED: 'Not declared' },
  fieldName: { name: 'Full name', document_number: 'ID number', birth: 'Date of birth' },
  sourceKind: {
    workflow: 'Workflow',
    registry_transaction: 'Registry command',
    registry_transaction_group: 'Registry commands',
    reconciliation_issue: 'Registry reconciliation',
    dossier: 'Domain dossier',
    dossier_sync: 'VNNIC dossier sync',
    vnnic_list_mismatch: 'VNNIC list comparison',
    coverage_gap: 'Compliance coverage',
    notification: 'E-mail log',
    webhook_endpoint: 'Webhook',
    name_change_request: 'Name change request',
    declaration_submission: 'Declaration',
    panel_operation: 'VNNIC management system step',
    subject_verification: 'Subject verification',
  },
  // A work item's finding as a sentence (lib/workSummary.ts), from its kind and data.
  summary: {
    code: ' (code {{code}})',
    vnnicRejected: { REJECTED: 'VNNIC rejected the dossier{{code}}.', CORRECTION_REQUIRED: 'VNNIC asks for the dossier to be corrected{{code}}.' },
    VNNIC_SYNC_MISSING: 'The dossier is complete but was never queued for VNNIC.',
    VNNIC_SYNC_DRIFT: 'The dossier differs from the package submitted to VNNIC.',
    VNNIC_SYNC_STUCK: 'The VNNIC sync has stayed “{{status}}” too long{{code}}.',
    DOSSIER_MANUAL_REVIEW: 'A policy in force requires a staff review of the dossier.',
    WORKFLOW_FAILED: '{{workflow}} failed at the “{{step}}” step{{code}}.',
    WORKFLOW_MANUAL_REVIEW: '{{workflow}} stopped at the “{{step}}” step for a person{{code}}.',
    registryTransaction: 'The registry command “{{operation}}” has no known outcome; reconciliation will settle it.',
    registryTransactionGrouped: '{{count}} commands with no known outcome at registry {{registry}}.',
    reconciliation: {
      NAMESERVER: 'Our name servers differ from the registry.',
      EXISTENCE: 'The domain exists on one side only (ours or the registry).',
      SPONSORSHIP: 'The registry shows another registrar as sponsor.',
      other: 'Our data differs from the registry ({{type}}).',
    },
    COVERAGE_GAP: 'Compliance gap: {{gap}}.',
    VNNIC_LIST_MISMATCH: 'The VNNIC domain list differs from ours: {{mismatch}}.',
    NOTIFICATION_FAILED: 'The “{{template}}” e-mail failed after {{attempts}} attempts.',
    VNNIC_PANEL_REGISTRY_LOCK: 'Set the domain lock statuses in the VNNIC management system.',
    VNNIC_PANEL_REGISTRY_UNLOCK: 'Clear the domain lock statuses in the VNNIC management system.',
    workflow: { DOMAIN_REGISTRATION: 'Registration', DOMAIN_RENEWAL: 'Renewal', DOMAIN_TRANSFER_IN: 'Transfer in', DOMAIN_RESTORE: 'Restore' },
    step: {
      dossier: 'dossier',
      gate: 'compliance check',
      zone: 'zone rules',
      prepare: 'preparation',
      contact: 'registry contact',
      registry_create: 'registry create',
      registry_renew: 'registry renewal',
      await_approval: 'awaiting registry approval',
      activate: 'activation',
      compensate: 'compensation',
      rollback: 'rollback',
      abandon: 'abandon',
      unhold: 'release of held funds',
    },
    operation: {
      domain_check: 'domain check',
      domain_create: 'domain create',
      domain_info: 'domain info',
      domain_update: 'domain update',
      domain_update_status: 'status update',
      domain_update_registrant: 'registrant change',
      domain_update_authinfo: 'transfer code change',
      domain_renew: 'renewal',
      domain_delete: 'domain delete',
      domain_restore: 'restore',
      domain_transfer_request: 'transfer request',
      domain_transfer_query: 'transfer query',
      domain_transfer_approve: 'transfer approval',
      domain_transfer_reject: 'transfer rejection',
      domain_transfer_cancel: 'transfer cancellation',
      contact_create: 'contact create',
      contact_info: 'contact info',
      host_create: 'name server create',
      host_update: 'name server update',
    },
    issue: { NAMESERVER: 'name servers', EXISTENCE: 'existence', SPONSORSHIP: 'sponsorship', UNKNOWN_OUTCOME: 'unknown outcome' },
    gap: {
      MISSING_DOSSIER: 'no dossier',
      INCOMPLETE_DOSSIER: 'incomplete dossier',
      VERIFICATION_NOT_VALID: 'subject verification not valid',
      SUBJECT_MISMATCH: 'subject mismatch',
      VNNIC_SYNC_MISSING: 'not synced to VNNIC',
      VNNIC_SYNC_PENDING: 'VNNIC sync pending',
      VNNIC_SYNC_REJECTED: 'VNNIC sync rejected',
      VNNIC_SYNC_ERROR: 'VNNIC sync error',
      REPORT_PROJECTION_MISSING: 'missing from the report set',
      VNNIC_REGISTRY_MISSING: 'not in the VNNIC list',
    },
    mismatch: { MISSING_AT_VNNIC: 'missing at VNNIC', UNKNOWN_LOCALLY: 'listed by VNNIC, unknown to us' },
    syncStatus: { QUEUED: 'queued', SUBMITTING: 'submitting', SUBMITTED: 'submitted, waiting for VNNIC', ACKNOWLEDGED: 'acknowledged', REJECTED: 'rejected', CORRECTION_REQUIRED: 'correction required' },
  },

  queue: {
    title: 'Work queue',
    subtitle: 'Everything that needs a person: reviews the platform would not decide alone, and operational exceptions.',
    note: 'The platform completes a dossier by itself only when everything matches. Nothing here is rejected or cancelled automatically; a registration waiting on an open review is not cancelled at its deadline. Items close by themselves when the problem goes away.',
    stats: { open: 'Open', mine: 'Assigned to me', unassigned: 'Unassigned', critical: 'Critical / high' },
    byKind: 'Open by kind',
    filter: {
      status: 'Status',
      kind: 'Kind',
      severity: 'Severity',
      assignee: 'Assignee',
      age: 'Older than',
      any: 'Any',
      me: 'Me',
      unassigned: 'Unassigned',
      hours: '{{count}} h',
      days: '{{count}} days',
      scoped: 'Showing items of one domain or registrant only.',
      clear: 'Show all',
    },
    col: { kind: 'Kind', severity: 'Severity', subject: 'About', summary: 'What was found', opened: 'Opened', assignee: 'Assignee', state: 'State' },
    empty: 'Nothing waits for a person.',
    noDomain: 'No domain',
  },

  item: {
    title: 'Work item',
    loadError: 'The work item could not be loaded.',
    facts: 'What was found',
    comparison: 'Comparison with the declared data',
    comparisonNote: 'Only the result per field is kept — never the values read from the identity card.',
    face: 'Face comparison',
    faceBand: 'Band',
    similarity: 'Similarity',
    threshold: 'Threshold',
    reasonCodes: 'Reason codes',
    changed: 'Changed parts',
    kind: 'Kind',
    severity: 'Severity',
    status: 'Status',
    source: 'Source',
    openSource: 'Open the source',
    openedBy: 'Reported by',
    opened: 'Opened',
    assignee: 'Assignee',
    assignedAt: 'Assigned',
    firstAction: 'First action',
    domain: 'Domain',
    dossier: 'Dossier',
    subject: 'Registrant',
    openSubject: 'Open registrant',
    openRequest: 'Open the request',
    due: 'Answer by',
    decidedOnRequest: 'This item is decided on its request (approve / reject / request correction, or the VNNIC panel step). It closes by itself when the registry shows the change.',
    verification: 'Verification',
    decision: 'Decision',
    decidedBy: 'Closed by',
    decidedAt: 'Closed',
    reason: 'Reason',
    effect: 'Effect',
    system: 'The system',
    assignMe: 'Assign to me',
    assignOther: 'Assign…',
    approve: 'Approve',
    reject: 'Reject',
    resolve: 'Resolve',
    raw: 'All recorded facts',
  },

  assign: {
    title: 'Assign',
    label: 'Staff member',
    submit: 'Assign',
  },
  approve: {
    title: 'Approve',
    description: {
      identity: 'Staff do not verify a registrant by hand: a registrant is verified only by VNNIC eKYC or a digitally signed document. Reject, or ask the registrant to redo the eKYC or sign the declaration digitally.',
      dossier: 'Completes the dossier with you as reviewer. Every other requirement must already be met; the registration then continues and still asks the ComplianceGate.',
      other: 'Acknowledges the finding. Nothing changes on the domain or its dossier.',
    },
    submit: 'Approve',
  },
  reject: {
    title: 'Reject',
    description: 'Sends the dossier back to the registrant (CORRECTION_REQUIRED) and e-mails them. The registration keeps waiting until the registrant corrects it or its deadline passes.',
    descriptionNoDossier: 'Records the rejection. There is no dossier to send back.',
    note: 'Message to the registrant (optional)',
    noteHint: 'Shown in the e-mail. Do not include identity numbers.',
    submit: 'Reject',
  },
  resolve: {
    title: 'Resolve',
    description: 'Closes the item with your reason. It does not change the domain, the order or the registry: do that on the source page first.',
    submit: 'Resolve',
  },
  form: {
    reason: 'Reason (internal, audited)',
    reasonHint: 'Required. Stored in the audit log with your name.',
    done: 'Done.',
  },

  linked: {
    title: 'Work queue',
    open_one: '{{count}} open item',
    open_other: '{{count}} open items',
    none: 'No open item.',
    view: 'View items',
  },

  webhooks: {
    title: 'Webhooks',
    platformOnly: 'platform only',
    groups: {
      work_item: 'Work items', domain: 'Domains', addon: 'Domain services', registrant_change: 'Name change / holder transfer (.vn)',
      registry_lock: 'VNNIC registry lock', transfer_in: 'Transfers in', transfer_out: 'Transfers out', registrant: 'Registrant (EPP)',
      restore: 'Restore', contact: 'Registry contacts', subject: 'Subjects', verification: 'Verification', dossier: 'Dossiers',
      vnnic_sync: 'VNNIC sync', order: 'Orders', invoice: 'Invoices', quote: 'Quotes', webhook: 'Webhook',
    },
    subtitle: 'Events sent to your systems (e.g. a CRM), signed with a secret.',
    customerTitle: 'Customer webhooks',
    customerSubtitle: 'Endpoints of this customer. They receive only events about objects the customer owns.',
    add: 'Add endpoint',
    edit: 'Edit endpoint',
    empty: 'No endpoint yet.',
    col: { endpoint: 'Endpoint', events: 'Events', state: 'State', lastSuccess: 'Last success' },
    allEvents: 'All events',
    enabled: 'Enabled',
    disabled: 'Disabled',
    autoDisabled: 'Disabled automatically',
    failing_one: '{{count}} failed attempt in a row',
    failing_other: '{{count}} failed attempts in a row',
    disableNote: 'An endpoint that fails {{failures}} attempts in a row over at least {{hours}} hours is disabled automatically; pending events wait until it is enabled again.',
    secretSet: 'Secret set',
    secretNone: 'No secret: deliveries are not signed',
    test: 'Send test event',
    testQueued: 'Test event queued.',
    form: {
      description: 'Description',
      url: 'URL',
      urlHint: 'Use https. Answer 2xx within 10 seconds.',
      events: 'Events',
      all: 'All events available to this owner',
      enabled: 'Enabled',
      secret: 'Signing secret',
      secretKeep: 'Keep the current secret',
      secretSet: 'Set a new secret',
      secretClear: 'Remove the secret (unsigned)',
      secretHint: '16 to 256 characters. It is never shown again; store it in the receiving system.',
      save: 'Save',
    },
    log: {
      title: 'Delivery log',
      col: { event: 'Event', endpoint: 'Endpoint', status: 'Status', attempts: 'Attempts', last: 'Last answer', created: 'Created' },
      empty: 'No delivery yet.',
      resend: 'Resend',
      resent: 'Queued again.',
      body: 'Body sent',
      status: { PENDING: 'Pending', SENDING: 'Sending', DELIVERED: 'Delivered', FAILED: 'Failed' },
    },
  },
} as const;
